import { useCallback, useEffect, useMemo, useState } from 'react';
import { Combobox, Field, Option, Skeleton, SkeletonItem } from '@fluentui/react-components';
import { fieldMeetCertainCriteriaStyles } from '../styles';
import { useAutomationRuleConditions } from '../../use-conditions';
import { ConditionItemType, FieldMeetCertainCriteriaType } from '../../types';
import { useIterationAndAreaPathField } from './use-iteration-and-area-path-field';
import { useAzureProvider } from 'src/azure/context';
import ClearebleButtonInput from 'src/components/shared/cleareble-button-input';

interface Props {
  id?: string;
  conditionId: string;
  validationError: string | null;
  isMultiSelect?: boolean;
}

export default function IterationAndAreaPathInput(props: Props) {
	const classes = fieldMeetCertainCriteriaStyles();
	const iterationAndAreaPathField = useIterationAndAreaPathField();
	const {applicationContext} = useAzureProvider();

	const [onInitialLoading, setOnInitialLoading] = useState(false);
	const {conditions, setConditions, resetValidationErrors} = useAutomationRuleConditions();

	const [options, setOptions] = useState<Array<{ value: string; label: string }>>([]);
	const [status, setStatus] = useState<'searching' | 'selected'>('selected');
	const [text, setText] = useState<string>('');

	const current: FieldMeetCertainCriteriaType = useMemo(() => {
		const foundConditions = conditions.find(item => item.id === props.conditionId) as ConditionItemType;
		return foundConditions!.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType;
	}, [conditions, props.conditionId]);

	const onChangeText = useCallback((value: string) => {
		setStatus('searching');
		setText(value);
		
		if (value === '') {
			getOptionsAsync();
		}
		
		if (value !== '') {
			const filteredOptions = options.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
			setOptions(filteredOptions);
		}
	}, [options]);

	const setOptionSelect = useCallback((value: string) => {
		if (value) {
			resetValidationErrors();
			const foundCondition = conditions.find(item => item.id === props.conditionId);
			setStatus('selected');
	
			if (foundCondition) {
				if (props.isMultiSelect) {
					setConditions(oldConditions => {
						const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
						const others = oldConditions.filter(item => item.id !== props.conditionId);

						const iterationOrAreaPathValue = current.fieldMeetCertainCriteria!.iterationOrAreaPathValue as Array<string>;

						const selectedValues = iterationOrAreaPathValue.includes(value) ?
							iterationOrAreaPathValue.filter(item => item !== value) :
							[...iterationOrAreaPathValue, value];

						return [...others, {
							...current, fieldMeetCertainCriteria: {
								...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
								iterationOrAreaPathValue: selectedValues
							}
						}];
					});
				}

				if (!props.isMultiSelect) {
					setConditions(oldConditions => {
						const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
						const others = oldConditions.filter(item => item.id !== props.conditionId);
		
						return [...others, {
							...current, fieldMeetCertainCriteria: {
								...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
								iterationOrAreaPathValue: value
							}
						}];
					});
				}
			}
		}
	}, [
		props.isMultiSelect
	]);
  
	const valueRender = useMemo(() => {
		const isSelected = current && current.iterationOrAreaPathValue && status === 'selected';

		if (isSelected && !props.isMultiSelect) {
			return options.find(item => item.value === current.iterationOrAreaPathValue)?.label || '';
		}

		if (isSelected && props.isMultiSelect && Array.isArray(current.iterationOrAreaPathValue)) {
			return current
				.iterationOrAreaPathValue
				.map(item => options.find(option => option.value === item)?.label || '')
				.join(', ');
		}

		if (text) {
			return text;
		}

		return '';
	}, [
		text,
		current.iterationOrAreaPathValue,
		options,
		status,
		props.isMultiSelect
	]);

	const selectedOptions = useMemo(() => {
		const isMultiSelect = props.isMultiSelect && Array.isArray(current.iterationOrAreaPathValue);

		if (isMultiSelect && current.iterationOrAreaPathValue.length > 0) {
			return current.iterationOrAreaPathValue as Array<string>;
		}

		if (isMultiSelect && current.iterationOrAreaPathValue.length > 0) {
			return [];
		}

		return [current.iterationOrAreaPathValue as string];
	}, [
		current,
		options,
		props.isMultiSelect
	]);

	const getOptionsAsync = useCallback(async () => {
		const resultOptions = await iterationAndAreaPathField.getIterationAndAreaPathOptions({
			organizationName: applicationContext.organizationName,
			projectId: applicationContext.projectId,
			workItemField: current.workItemField,
		});

		setOptions(resultOptions);
	}, [current.workItemField]);

	const clearOptionSelect = useCallback(() => {
		const foundCondition = conditions.find(item => item.id === props.conditionId);
		setStatus('selected');

		if (foundCondition) {
			setConditions(oldConditions => {
				const current = oldConditions.find(item => item.id === props.conditionId) as ConditionItemType;
				const others = oldConditions.filter(item => item.id !== props.conditionId);

				return [...others, {
					...current, fieldMeetCertainCriteria: {
						...current.fieldMeetCertainCriteria as FieldMeetCertainCriteriaType,
						iterationOrAreaPathValue: ''
					}
				}];
			});
		}
	}, [conditions, props.conditionId]);

	useEffect(() => {
		setOnInitialLoading(true);
		getOptionsAsync()
			.finally(() => {
				setOnInitialLoading(false);
			});
	}, []);

	if (onInitialLoading) {
		return (
			<Skeleton className={classes.skeletonRoot}>
				<SkeletonItem className={classes.skeletonItem} />
			</Skeleton>
		);
	}
  
	return (
		<Field
			className={classes.dropdownRoot}
			validationMessage={props.validationError}
		>
			<Combobox
				clearable
				autoComplete='off'
				inputMode='search'
				className={classes.dropdown}
				appearance='outline'
				placeholder='Choose a value'
				positioning="below-start"
				value={valueRender}
				selectedOptions={selectedOptions}
				clearIcon={<ClearebleButtonInput onClick={clearOptionSelect} />}
				multiselect={props.isMultiSelect}
				onOptionSelect={(_, data) => {
					setOptionSelect(data.optionValue as string);
				}}
				onChange={(ev) => {
					onChangeText(ev.target.value);
				}}
				onBlur={() => {
					setStatus('selected');
					getOptionsAsync();
				}}
			>
				<div style={{maxHeight: 240}}>
					{options.map((item, idx) => (
						<Option key={idx} value={item.value}>{item.label}</Option>
					))}
				</div>
			</Combobox>
		</Field>
	);
}